import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback } from "react";

import { AccordionButton } from "@/components/Button/AccordionButton";
import ActionButton from "@/components/Button/ActionButton";
import { AttributeCard } from "@/components/Card/AttributeCard";
import Icon from "@/components/Icon/Icon";
import type { IconName } from "@/components/Icon/iconMap";
import { Grid, Spacer, VStack } from "@/components/Layout";
import StepperHeaderComponent from "@/components/StepperHeader";
import { Text } from "@/components/Typography/Text";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { CustomAppType } from "@/constants/customApp";
import { cn } from "@/lib/cn";
import { defaultNS, useTranslation } from "@/lib/i18n";

import { ManualFormHash } from "./-constants/constant";

export const Route = createFileRoute("/_staff/apps/custom-app/add/")({
  component: Introduce,
});

function Introduce() {
  return (
    <VStack className="mx-auto w-[1000px] ">
      <Header />
      <Spacer size={24} />
      <CustomAppTypeRadioGroup />
      <Spacer size={24} />
      <AdditionalOptions />
    </VStack>
  );
}

function Header() {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "pages.apps.customApp.add.intro.header",
  });

  return (
    <VStack>
      <Spacer variant="xxl" />
      <StepperHeaderComponent headerTitle={t("headerTitle")} />
    </VStack>
  );
}

function CustomAppTypeRadioGroup() {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "pages.apps.customApp.add",
  });

  const CUSTOM_APP_TYPES: {
    id: CustomAppType;
    title: string;
    requiredDetails: { iconName: IconName; text: string }[];
    isDisabled: boolean;
  }[] = [
    {
      id: CustomAppType.CUSTOM_APP,
      title: t("itemCard.title_custom_app"),
      requiredDetails: [
        {
          iconName: "SquareDot",
          text: t("form.navigation.intro.customAppNameLabel"),
        },
        {
          iconName: "SquareDot",
          text: t("form.navigation.intro.appCategoryLabel"),
        },
        {
          iconName: "SquareDot",
          text: t("form.navigation.intro.pricingLabel"),
        },
      ],
      isDisabled: false,
    },
  ];

  const navigate = useNavigate();

  const handleButtonClick = useCallback(() => {
    navigate({
      to: `/apps/custom-app/add/${CustomAppType.CUSTOM_APP}`,
      hash: ManualFormHash.BASIC,
    });
  }, [navigate]);

  return (
    <>
      <Spacer variant="xl" />
      <Text variant="h4"> {t("selectCustomAppType")}</Text>
      <Spacer variant="l" />
      <RadioGroup onValueChange={() => {}}>
        <Grid columns={2} rowGap={28} columnGap={24}>
          {CUSTOM_APP_TYPES.map((type) => (
            <AttributeCard
              key={type.id}
              title={type.title}
              titleStyle="CardHeader"
              onClick={() => {}}
              actionButton={
                <RadioGroupItem
                  id={type.id}
                  value={type.id}
                  className="h-4 w-4"
                  checked={true}
                />
              }
              actionButtonPosition="top-left"
              actionButtonIsInner={false}
              className={cn(
                type.isDisabled
                  ? "cursor-not-allowed border border-neutral-muted-border bg-neutral-fill-disabled"
                  : "border border-neutral-muted-border",
              )}
            >
              <VStack className="gap-3">
                <Text variant="labelSmall">
                  {t("itemCard.requiredDetails")}
                </Text>
                {type.requiredDetails.map((detail, index) => (
                  <Icon
                    key={`${detail.text}-${index}`}
                    iconName={detail.iconName}
                    text={detail.text}
                  />
                ))}
              </VStack>
            </AttributeCard>
          ))}
        </Grid>
      </RadioGroup>
      <Spacer variant="xl" />
      <ActionButton
        icon="FilePenIcon"
        className="w-48"
        onClick={handleButtonClick}
      >
        {t("fillOutDetails")}
      </ActionButton>
    </>
  );
}

function AdditionalOptions() {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "pages.devices.add.intro.options",
  });

  return (
    <VStack className="w-full">
      <Text variant="labelSemiLarge">{t("description")}</Text>
      <Spacer size={24} />
      <AccordionButton
        icon={
          <Icon
            iconName="DatabaseBackup"
            text={t("syncIT")}
            textColor="text-primary"
            colorVariant="primary"
          />
        }
        inDevelopment={true}
        className="w-full"
      />
      <Spacer size={24} />
    </VStack>
  );
}
